import { Fuel, TrendingUp, TrendingDown, Minus, RefreshCw } from 'lucide-react';
import { useState, useEffect } from 'react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TrendIcon = ({ current, previous }) => {
  if (!current || !previous) return <Minus size={14} className="trend-icon neutral" />;
  const diff = current - previous;
  if (Math.abs(diff) < 0.1) return <Minus size={14} className="trend-icon neutral" />;
  if (diff > 0) return <TrendingUp size={14} className="trend-icon up" />;
  return <TrendingDown size={14} className="trend-icon down" />;
};

// SVG Sparkline component
const Sparkline = ({ data, color = '#FFC107', height = 40, width = '100%' }) => {
  if (!data || data.length < 2) return null;
  
  const values = data.map(d => d || 0).reverse(); // oldest first
  const min = Math.min(...values) - 1;
  const max = Math.max(...values) + 1;
  const range = max - min || 1;
  
  const viewWidth = 200;
  const viewHeight = height;
  const padding = 4;
  
  const points = values.map((val, i) => {
    const x = padding + (i / (values.length - 1)) * (viewWidth - padding * 2);
    const y = viewHeight - padding - ((val - min) / range) * (viewHeight - padding * 2);
    return `${x},${y}`;
  });
  
  const linePoints = points.join(' ');
  // Fill area under curve
  const areaPoints = `${padding},${viewHeight - padding} ${linePoints} ${viewWidth - padding},${viewHeight - padding}`;
  
  return (
    <svg viewBox={`0 0 ${viewWidth} ${viewHeight}`} width={width} height={height} preserveAspectRatio="none">
      <polygon points={areaPoints} fill={color} opacity="0.1" />
      <polyline points={linePoints} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      {/* Dot on latest value */}
      {points.length > 0 && (
        <circle cx={points[points.length - 1].split(',')[0]} cy={points[points.length - 1].split(',')[1]} r="3" fill={color} />
      )}
    </svg>
  );
};

const PetrolPricesSection = () => {
  const [current, setCurrent] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchPrices = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/petrol/prices`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setCurrent(data?.current || null);
      setHistory(data?.history || []);
    } catch (err) {
      console.error('Fuel price fetch error:', err);
      // Retry once after 3s on failure
      setTimeout(async () => {
        try {
          const res = await fetch(`${API}/petrol/prices`);
          if (res.ok) {
            const data = await res.json();
            setCurrent(data?.current || null);
            setHistory(data?.history || []);
          }
        } catch (retryErr) {
          console.error('Fuel price retry failed:', retryErr);
        }
        setLoading(false);
      }, 3000);
      return;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrices();
  }, []);

  const prevWeek = history.length > 1 ? history[1] : null;

  const formatChange = (curr, prev) => {
    if (!curr || !prev) return null;
    const diff = (curr - prev).toFixed(1);
    if (Math.abs(parseFloat(diff)) < 0.1) return 'No change';
    return parseFloat(diff) > 0 ? `+${diff}p` : `${diff}p`;
  };

  const unleadedHistory = history.map(h => h?.unleaded_avg).filter(Boolean);
  const dieselHistory = history.map(h => h?.diesel_avg).filter(Boolean);

  return (
    <div className="content-card content-card-large" data-testid="petrol-section">
      <div className="card-header">
        <h2><Fuel size={20} className="inline" /> Fuel Prices - King's Lynn</h2>
        <button className="section-action-btn" onClick={fetchPrices} title="Refresh">
          <RefreshCw size={16} className={loading ? 'spinning' : ''} />
        </button>
      </div>
      <div className="card-content">
        {loading && !current ? (
          <div className="loading-placeholder">Loading fuel prices...</div>
        ) : !current ? (
          <div className="empty-state">Fuel prices unavailable</div>
        ) : (
          <>
            <div className="fuel-prices-grid" data-testid="fuel-current-prices">
              {current.unleaded_avg && (
                <div className="fuel-price-card">
                  <div className="fuel-label">Unleaded Avg</div>
                  <div className="fuel-value">
                    {current.unleaded_avg}p
                    <TrendIcon current={current.unleaded_avg} previous={prevWeek?.unleaded_avg} />
                  </div>
                  {formatChange(current.unleaded_avg, prevWeek?.unleaded_avg) && (
                    <div className={`fuel-change ${current.unleaded_avg > (prevWeek?.unleaded_avg || 0) ? 'up' : 'down'}`}>
                      {formatChange(current.unleaded_avg, prevWeek?.unleaded_avg)}
                    </div>
                  )}
                  {unleadedHistory.length >= 2 && (
                    <div className="fuel-sparkline">
                      <Sparkline data={unleadedHistory} color="#FFC107" height={30} />
                    </div>
                  )}
                </div>
              )}
              {current.diesel_avg && (
                <div className="fuel-price-card">
                  <div className="fuel-label">Diesel Avg</div>
                  <div className="fuel-value">
                    {current.diesel_avg}p
                    <TrendIcon current={current.diesel_avg} previous={prevWeek?.diesel_avg} />
                  </div>
                  {formatChange(current.diesel_avg, prevWeek?.diesel_avg) && (
                    <div className={`fuel-change ${current.diesel_avg > (prevWeek?.diesel_avg || 0) ? 'up' : 'down'}`}>
                      {formatChange(current.diesel_avg, prevWeek?.diesel_avg)}
                    </div>
                  )}
                  {dieselHistory.length >= 2 && (
                    <div className="fuel-sparkline">
                      <Sparkline data={dieselHistory} color="#60a5fa" height={30} />
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="fuel-cheapest" data-testid="fuel-cheapest">
              {current.cheapest_unleaded && (
                <div className="fuel-cheapest-item">
                  <span className="cheapest-label">Cheapest Unleaded:</span>
                  <span className="cheapest-price">{current.cheapest_unleaded}p</span>
                  {current.cheapest_unleaded_station && (
                    <span className="cheapest-station">at {current.cheapest_unleaded_station}</span>
                  )}
                </div>
              )}
              {current.cheapest_diesel && (
                <div className="fuel-cheapest-item">
                  <span className="cheapest-label">Cheapest Diesel:</span>
                  <span className="cheapest-price">{current.cheapest_diesel}p</span>
                  {current.cheapest_diesel_station && (
                    <span className="cheapest-station">at {current.cheapest_diesel_station}</span>
                  )}
                </div>
              )}
              {current.cheapest_super && (
                <div className="fuel-cheapest-item">
                  <span className="cheapest-label">Cheapest Super:</span>
                  <span className="cheapest-price">{current.cheapest_super}p</span>
                </div>
              )}
            </div>

            {history.length > 1 && (
              <div className="fuel-history" data-testid="fuel-history">
                <h3 className="fuel-history-title">Weekly Tracker</h3>
                <table className="fuel-history-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Unleaded</th>
                      <th>Diesel</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((week) => (
                      <tr key={week.date}>
                        <td>{new Date(week.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</td>
                        <td>{week.unleaded_avg ? `${week.unleaded_avg}p` : '-'}</td>
                        <td>{week.diesel_avg ? `${week.diesel_avg}p` : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="fuel-source">
              Source: fuelcosts.co.uk (UK Gov CMA data)
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PetrolPricesSection;
